'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { getNewBuildingRatings, getNewNeighborhoodRatings, type Building } from '@repo/api';
import useIntersectionObserver from '@/hooks/useIntersectionObserver';
import { useAnalytics } from '@/hooks/useAnalytics';
import { RatingsSection } from './RatingsSection';
import { RatingsSectionSkeleton } from './RatingsSectionSkeleton';

interface RatingsProps {
  building: Building;
  buildingAddress: string;
  showSkeleton: boolean;
  isReportReady: boolean;
}

function Ratings({ building, buildingAddress, showSkeleton, isReportReady }: RatingsProps) {
  const analytics = useAnalytics();
  const sectionRef = useRef<HTMLDivElement>(null);
  const onScreen = useIntersectionObserver<HTMLDivElement>(
    sectionRef,
    useMemo(() => ({ threshold: 0.5 }), []),
  );

  const [loading, setLoading] = useState(true);
  const [buildingRatings, setBuildingRatings] = useState<any | null>(null);
  const [neighborhoodRatings, setNeighborhoodRatings] = useState<any | null>(null);

  useEffect(() => {
    let timer: number | undefined;
    if (onScreen) {
      timer = window.setTimeout(() => {
        analytics.track('rentalreport_buildingneighbor_ratings_scroll');
      }, 5000);
    }
    return () => {
      if (timer) window.clearTimeout(timer);
    };
  }, [analytics, onScreen]);

  useEffect(() => {
    if (!isReportReady) {
      setLoading(false);
      setBuildingRatings(null);
      setNeighborhoodRatings(null);
      return;
    }

    (async () => {
      setLoading(true);
      try {
        const [bRes, nRes] = await Promise.all([
          getNewBuildingRatings(building.slug),
          getNewNeighborhoodRatings(building.neighborhood.id),
        ]);

        setBuildingRatings(bRes);
        setNeighborhoodRatings(nRes);
      } catch (err) {
        console.error('[report][ratings] failed to load ratings', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [isReportReady, building.slug, building.neighborhood.id]);

  const shouldShowSkeleton = showSkeleton || loading;
  if (shouldShowSkeleton) return <RatingsSectionSkeleton />;

  return (
    <div ref={sectionRef}>
      <RatingsSection
        buildingAddress={buildingAddress}
        neighborhoodName={building.neighborhood?.name}
        buildingRatings={buildingRatings}
        neighborhoodRatings={neighborhoodRatings}
        isBlurRequired={!isReportReady}
      />
    </div>
  );
}

export default Ratings;
